import { useMemo, useState } from 'react';
import { Group, createStyles } from '@mantine/core';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';
import { UnstyledButton } from '~/domains/design-system/Button';

import type { FieldDescriptor, StaticFieldProps } from '../../types/common';
import type { FieldQuery } from '../../../context/types/store';

import type { APISegmentsFieldType, SegmentsFieldItemType, SegmentsFieldType } from './types';
import { SegmentsEdit } from './SegmentsEdit/SegmentsEdit';
import { SegmentsView } from './SegmentsView';

const useStyles = createStyles((theme) => ({
  heading: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    padding: `${theme.spacing.xs / 2}px 0`,
  },
  actionButton: {
    color: theme.colors.blue[7],
  },
}));

function SegmentsFieldElement({
  field,
  value,
  onValueChange,
}: StaticFieldProps<SegmentsFieldType>) {
  const { classes } = useStyles();
  const [isEditing, setIsEditing] = useState(false);

  const segments = useMemo(
    () =>
      ((value as string[][]) ?? field.defaultValue)
        .map((slateIds, index) => ({
          key: `${index}`,
          label: `Segment ${index + 1}`,
          items: slateIds
            .map((slateId) => {
              const slate = field.slates.find((item) => item.key === slateId);
              return slate;
            })
            .filter(Boolean),
        }))
        .filter(Boolean),
    [field.slates, value, field.defaultValue]
  );

  return (
    <div data-test-id={dt.segments.container}>
      <Group position="apart" className={classes.heading}>
        <Text variant="subhead-medium">{field.label}</Text>
        <Text>{field.description}</Text>
        <UnstyledButton
          className={classes.actionButton}
          data-test-id={isEditing ? dt.segments.action.save : dt.segments.action.edit}
          onClick={() => {
            setIsEditing((v) => !v);
          }}
        >
          <Text variant="subhead-small">{isEditing ? 'Save segments' : 'Customize segments'}</Text>
        </UnstyledButton>
      </Group>
      {isEditing ? (
        <SegmentsEdit
          slates={field.slates}
          rules={field.rules}
          value={(value as string[][]) ?? field.defaultValue}
          onChange={onValueChange}
          slateLabel={field.slateLabel}
        />
      ) : (
        <SegmentsView segments={segments} slateLabel={field.slateLabel} />
      )}
    </div>
  );
}

const Descriptor: FieldDescriptor<APISegmentsFieldType, SegmentsFieldType> = {
  FieldComponent: SegmentsFieldElement,
  name: 'SegmentsField',
  parseFieldResponse: ({ field, fieldKey }) => {
    const slates: SegmentsFieldItemType[] = field.slates.map((slate) => ({
      ...slate,
      date: new Date(slate.date),
    }));

    const normalizedField: FieldQuery = {
      key: fieldKey,
      type: field.type,
      data: {
        ...field,
        slates,
      },
    };

    return [normalizedField];
  },
};

export default Descriptor;
